import { BaseSyntheticEvent, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { path, UserInfo } from "../common/Common";
import UserInfoItem from "./UserInfoItem";
import ContentTitle from "../common/ContentTitle";
import UserInfoInput from "./UserInfoInput";
import Button from "../common/Button";
import styles from "./PasswordForm.module.css";
import { DecodeParams, PasswordQueryParams, PasswordResetParams } from "../../data_provider/gateways/UserGateway";
import { ResponseType } from "../../data_provider/gateways/Gateway";
import { useNavigate, useLocation } from "react-router";
import DataProvider from "../../data_provider/DataProvider";
import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import { Helmet } from "react-helmet-async";
import { emitAuthEvent } from "../../module/auth";

interface PasswordFormProps {
    data: UserInfo;
    title: string;
    onSubmit: (data: PasswordQueryParams) => Promise<ResponseType>;
    onDismiss: () => void;
    mode?: PasswordFormType;
}

// 폼 동작 모드
export enum PasswordFormType {
    Verify = "verify", // 마이페이지(로그인상태)에서 기존 비밀번호 확인만 필요할 때
    Change = "change", // 마이페이지(로그인상태)에서 비밀번호 변경
    Reset = "reset", // 비밀번호 찾기 후 비로그인 상태에서 재설정
    Withdrawn = "widthdrawn",
}

enum InputType {
    Input = "input",
    Confirm = "confirm",
    Prev = "prev",
}

/**
 * @author bakim, YoungJunKim
 * @date 2024-09-11 10:56:38, 2025-03-24
 * @remark 비밀번호 입력/확인 폼
 * @param  {UserInfo} data: (required) 사용자 정보
 * @param  {string} title: (required) 폼 타이틀
 * @param  {(data: PasswordQueryParams) => Promise<ResponseType>} onSubmit: (required) 확인 버튼 선택(클릭) 시 콜백 함수
 * @param  {() => void} onDismiss: (required) 취소 버튼 선택(클릭) 시 콜백 함수
 * @param  {PasswordFormType} mode?: 비밀번호 폼 모드
 * @returns UserInfoArea
 */
const PasswordForm = (props: PasswordFormProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { ModalComponent, openModal, closeModal } = useModal();

    const defaultMsg = "";

    const [password, setPassword] = useState("");
    const [inputPassword, setInputPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [userId, setUserId] = useState(props.data.userId);

    const [validation, setValdation] = useState<boolean>(false);
    const [messages, setMessages] = useState({ password: defaultMsg, inputPassword: defaultMsg, confirmPassword: defaultMsg });

    const queryParams = new URLSearchParams(location.search);
    const code = props.mode === PasswordFormType.Reset ? queryParams.get("code") : null;

    const [, setCookie, removeCookie] = useCookies(["KMUN", "KMLN"]);

    // 25.08.27 | Kim Young Jun | 소셜 로그인 사용자 회원탈퇴 비밀번호 입력 없이 활성화
    // 유효성 판단 (입력값 변경 시)
    // useEffect(() => {
    //     // Verify/Withdrawn Mode
    //     if (props.mode === PasswordFormType.Verify || props.mode === PasswordFormType.Withdrawn) {
    //         // 소셜 로그인 사용자(platform != 1)는 비밀번호 입력 없이 바로 활성화
    //         if (props.data.platform !== 1) {
    //             setValdation(true);
    //         } else {
    //             // 웹 플랫폼 사용자(platform == 1)는 비밀번호 입력 필요
    //             setValdation(password.length > 0);
    //         }
    //         return;
    //     }

    //     // Change/Reset Mode
    //     if (confirmPassword === "") return;
    //     let isValid: boolean = Object.values(messages).filter((value) => value !== defaultMsg).length === 0;

    //     if (props.mode == PasswordFormType.Change) {
    //         isValid = password === "" ? false : isValid;
    //     }

    //     setValdation(isValid);
    // }, [messages, password, props.data.platform, props.mode]);
    
    // hmchoi
    useEffect(() => {
        // Verify / Withdrawn
        if (
            props.mode === PasswordFormType.Verify ||
            props.mode === PasswordFormType.Withdrawn
        ) {
            if (props.data.platform !== 1) {
                setValdation(true);
            } else {
                setValdation(password.length > 0);
            }

            return;
        }

        // Reset
        if (props.mode === PasswordFormType.Reset) {
            const isValid =
                inputPassword.length > 0 &&
                confirmPassword.length > 0 &&
                inputPassword === confirmPassword &&
                messages.inputPassword === defaultMsg &&
                messages.confirmPassword === defaultMsg;

            setValdation(isValid);
            return;
        }

        // Change
        if (props.mode === PasswordFormType.Change) {
            const isValid =
                password.length > 0 &&
                inputPassword.length > 0 &&
                confirmPassword.length > 0 &&
                inputPassword === confirmPassword &&
                messages.inputPassword === defaultMsg &&
                messages.confirmPassword === defaultMsg &&
                messages.password === defaultMsg;

            setValdation(isValid);
        }
    }, [
        password,
        inputPassword,
        confirmPassword,
        messages,
        props.data.platform,
        props.mode,
    ]);

    // 25.03.25 | Kim Young Jun | decode request 추가
    useEffect(() => {
        const fetchDecodedEmail = async () => {
            if (props.mode === PasswordFormType.Reset && code) {
                const requestParams = {
                    code: code,
                } as DecodeParams;

                const response = await DataProvider.GetDecode(requestParams);

                if (response.flag) {
                    setUserId(response.result.userId);
                    console.log("Decoded userId:", response.result.userId);
                } else {
                    setUserId("사용자");
                }
            }
        };

        fetchDecodedEmail();
    }, [props.mode, code]);

    const resetPassword = async (data: PasswordResetParams): Promise<ResponseType> => {
        return await DataProvider.ResetPassword(data);
    };

    const logoutStub = async () => {
        await DataProvider.Logout();
        emitAuthEvent("logout"); // 로그아웃 시 모든 탭에 알림
        // KMLN은 프론트에서 추가/변경하는 쿠키, 로그인 여부 판별용
        removeCookie("KMLN");
        removeCookie("KMUN");
        navigate(path.ROOT, { replace: true });
    };

    const onClickSumitPortalCheck = async () => {
        openModal({
            title: "안내",
            children: "정말 탈퇴하시겠습니까?",
            modalButtons: (
                <ModalButtons
                    buttonCount={2}
                    buttons={[
                        {
                            label: "취소",
                            color: "Secondary",
                            onClick: () => {
                                closeModal();
                            },
                        },
                        {
                            label: "탈퇴",
                            color: "Danger",
                            onClick: () => {
                                onClickSumit();
                                closeModal();
                            },
                        },
                    ]}
                />
            ),
        });
    };

    // 25.08.27 | Kim Young Jun | 소셜 로그인 사용자 회원탈퇴 시 비밀번호 전송 로직 수정
    // submit 핸들러
    const onClickSumit = async () => {
        const data = {
            newPassword: props.mode === PasswordFormType.Verify ? "" : inputPassword,
            password:
                props.mode === PasswordFormType.Change || props.mode === PasswordFormType.Verify || props.mode === PasswordFormType.Withdrawn
                    ? // 소셜 로그인 사용자(platform != 1)는 빈 비밀번호 전송
                      props.data.platform !== 1 && (props.mode === PasswordFormType.Verify || props.mode === PasswordFormType.Withdrawn)
                        ? ""
                        : password
                    : inputPassword,
        };

        let result: ResponseType;

        if (props.mode === PasswordFormType.Reset) {
            const reqParams = {
                code: code,
                newPassword: data.newPassword,
            } as PasswordResetParams;

            result = await resetPassword(reqParams);
        } else {
            result = await props.onSubmit(data);
        }

        if (result.flag) {
            const message = props.mode === PasswordFormType.Withdrawn ? "회원 탈퇴가 완료되었습니다." : "비밀번호가 변경되었습니다.";
            openModal({
                title: "안내",
                children: message,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    props.mode === PasswordFormType.Withdrawn ? logoutStub() : props.onDismiss();
                                    navigate(path.ROOT);
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        } else {
            // 실패 시 모달 표시
            let errorMessage = "";

            if (props.mode === PasswordFormType.Reset) {
                errorMessage = result.desc || "비밀번호 재설정에 실패했습니다.";
            } else if (props.mode === PasswordFormType.Change) {
                errorMessage = "현재 비밀번호를 정확히 입력해주세요.";
            } else if (props.mode === PasswordFormType.Verify) {
                errorMessage = "비밀번호를 정확히 입력해주세요.";
            } else if (props.mode === PasswordFormType.Withdrawn) {
                errorMessage = "회원 탈퇴 처리에 실패했습니다.";
            } else {
                errorMessage = result.desc || "처리 중 오류가 발생했습니다.";
            }

            openModal({
                title: "오류",
                children: errorMessage,
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });

            // 추가로 입력 필드 에러 메시지도 설정 (비밀번호 확인 관련)
            if (result.status === 403) {
                const _messages = { ...messages };
                if (props.mode === PasswordFormType.Change || props.mode === PasswordFormType.Verify || props.mode === PasswordFormType.Withdrawn) {
                    _messages.password = "* 현재 비밀번호를 정확히 입력해주세요.";
                }
                setMessages(_messages);
            }
        }
    };

    // Input 핸들러
    const handleInputChange = (e: BaseSyntheticEvent) => {
        const elem: HTMLInputElement = e.currentTarget;

        if (elem) {
            const type = elem.name as InputType;
            const value = elem.value;

            switch (type) {
                case InputType.Input:
                    setInputPassword(value);
                    break;
                case InputType.Confirm:
                    setConfirmPassword(value);
                    break;
                case InputType.Prev:
                    messages.password && setMessages({ ...messages, password: defaultMsg });
                    setPassword(value);
                    return;
            }
            validationCheck(value, type);
        }
    };

    /**
     * 비밀번호 확인, 입력, 재입력란 유효성 확인 함수
     * @param value 입력값
     * @param type 입력란 종류
     */
    const validationCheck = (value: string, type: InputType) => {
        const passwordRegex = new RegExp(/^(?=.*[0-9])(?=.*[a-zA-Z])[a-zA-Z0-9!@#$%^*+=-]{8,16}$/);
        const _messages = { ...messages };
        switch (type) {
            case InputType.Input:
                _messages.inputPassword =
                    (props.mode === PasswordFormType.Change || props.mode === PasswordFormType.Reset) && !passwordRegex.test(value)
                        ? "* 8 ~ 16자리의 영어, 숫자가 포함된 형태로 입력해주세요."
                        : defaultMsg;
                _messages.confirmPassword = confirmPassword === value || !confirmPassword ? defaultMsg : "* 비밀번호가 동일하지 않습니다.";
                break;
            case InputType.Confirm:
                _messages.confirmPassword = inputPassword === value ? defaultMsg : "* 비밀번호가 동일하지 않습니다.";
                break;
        }
        setMessages(_messages);
    };

    return (
        <>
            <Helmet>
                <title>비밀번호 변경 - 모두의지도</title>
                <meta name="description" content="비밀번호를 변경하세요" />
            </Helmet>
            <div className={styles.container}>
                <ModalComponent />

                {/* 제목 */}
                {props.title && <ContentTitle title={props.title} />}

                {/* 계정 정보 */}
                <UserInfoItem id="userId" label="계정" value={userId} />
                {props.mode === PasswordFormType.Change && <UserInfoItem id="username" label="별명" value={props.data.username} />}

                {/* 현재 비밀번호 입력 (Change, Verify, Withdrawn) */}
                {props.data.platform == 1 &&
                    (props.mode === PasswordFormType.Change ||
                        props.mode === PasswordFormType.Verify ||
                        props.mode === PasswordFormType.Withdrawn) && (
                        <UserInfoInput
                            name={InputType.Prev}
                            label="현재 비밀번호"
                            type="password"
                            onChange={handleInputChange}
                            cautionMsg={messages.password}
                        />
                    )}

                {/* 신규 비밀번호 입력 (Change, Reset) */}
                {(props.mode === PasswordFormType.Change || props.mode === PasswordFormType.Reset) && (
                    <UserInfoInput
                        name={InputType.Input}
                        label={props.mode === PasswordFormType.Change ? "신규 비밀번호" : "비밀번호"}
                        type="password"
                        onChange={handleInputChange}
                        cautionMsg={messages.inputPassword}
                    />
                )}

                {/* 비밀번호 확인 입력 (Change, Reset) */}
                {(props.mode === PasswordFormType.Change || props.mode === PasswordFormType.Reset) && (
                    <UserInfoInput
                        name={InputType.Confirm}
                        label="비밀번호 확인"
                        type="password"
                        onChange={handleInputChange}
                        cautionMsg={messages.confirmPassword}
                    />
                )}

                {/* 버튼 영역 */}
                <div className={styles.bottom}>
                    <Button
                        label={props.title.length >= 2 ? props.title.slice(-2) : props.title}
                        onClick={validation ? (props.mode === PasswordFormType.Withdrawn ? onClickSumitPortalCheck : onClickSumit) : undefined}
                        background={validation ? undefined : "#d1d1d1"}
                        cursor={validation ? undefined : "default"}
                    />
                    <Button label="취소" onClick={props.onDismiss} />
                </div>
            </div>
        </>
    );
};

export default PasswordForm;
